import React, { lazy, Suspense, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import { usePageSEO } from './utils/seoUtils';
import { SkipToMainContent, applyFocusVisiblePolyfill } from './utils/accessibilityUtils';

// Lazy load all section components
const Hero = lazy(() => import('./components/sections/Hero'));
const About = lazy(() => import('./components/sections/About'));
const HowItWorks = lazy(() => import('./components/sections/HowItWorks'));
const ProductCategories = lazy(() => import('./components/sections/ProductCategories'));
const TechnicalSpecs = lazy(() => import('./components/sections/TechnicalSpecs'));
const WhyChooseUs = lazy(() => import('./components/sections/WhyChooseUs'));
const Testimonials = lazy(() => import('./components/sections/Testimonials'));
const Contact = lazy(() => import('./components/sections/Contact'));

// Loading component for suspense fallback
const SectionLoader: React.FC = () => (
  <div className="min-h-[400px] flex items-center justify-center" role="status" aria-label="Loading section">
    <div className="animate-pulse">
      <div className="h-8 w-32 bg-gray-200 rounded mb-4 mx-auto"></div>
      <div className="h-4 w-64 bg-gray-200 rounded"></div>
    </div>
    <span className="sr-only">Loading...</span>
  </div>
);

function App() {
  // Apply SEO metadata
  usePageSEO({
    title: 'Arctic Atlas - Premium Heavy Duty Apparel & Industrial Textiles',
    description: 'Arctic Atlas specializes in premium heavy-duty apparel and industrial textiles. Discover our innovative materials, advanced manufacturing, and sustainable solutions for extreme conditions.',
    keywords: 'heavy duty apparel, industrial textiles, Arctic Atlas, premium workwear, technical fabrics, sustainable textiles, extreme weather gear',
  });

  // Apply focus visible polyfill for better accessibility
  useEffect(() => {
    applyFocusVisiblePolyfill();
  }, []);

  return (
    <div className="App">
      <SkipToMainContent />
      <Header />
      <main className="pt-16" id="main-content">
        <Suspense fallback={<SectionLoader />}>
          <Hero />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <About />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <HowItWorks />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <ProductCategories />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <TechnicalSpecs />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <WhyChooseUs />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <Testimonials />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <Contact />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default App;